/**
 * Abfragen und Aufrufe zur Anmeldung.
 *
 * Setzt die Endpunkte aus backend/app/api/v1/auth.py um. Besonderheit: `POST
 * /auth/login` antwortet mit zwei verschiedenen Formen — Token oder Aufforderung
 * zum zweiten Faktor. Das ist hier über `isMfaChallenge` unterschieden und nicht
 * über eine Vermutung anhand des HTTP-Status.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { queryKeys } from "./queryClient";
import { request, sessionFromToken, setSession } from "./client";
import type { LoginIn, LoginResult, MeOut, MfaVerifyIn, TokenOut } from "./types";
import { isMfaChallenge } from "./types";

/** Angemeldetes Konto samt wirksamen Rechten. */
export function useMe(enabled: boolean): UseQueryResult<MeOut, Error> {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => request<MeOut>("/auth/me"),
    enabled,
    // Rechte ändern sich selten, aber eine Rollenänderung soll spätestens beim
    // nächsten Start greifen — deshalb kein unbegrenzter Zwischenspeicher.
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  return useMutation<LoginResult, Error, LoginIn>({
    mutationFn: (payload) => request<LoginResult>("/auth/login", { method: "POST", body: payload, auth: false }),
    onSuccess: (result) => {
      // Bei Aufforderung zum zweiten Faktor darf **keine** Sitzung entstehen:
      // Ein Zugangstoken vor Abschluss des zweiten Faktors wäre der Faktor selbst.
      if (isMfaChallenge(result)) return;
      setSession(sessionFromToken(result));
    },
  });
}

export function useVerifyMfa() {
  return useMutation<TokenOut, Error, MfaVerifyIn>({
    mutationFn: (payload) =>
      request<TokenOut>("/auth/mfa/verify", { method: "POST", body: payload, auth: false }),
    onSuccess: (token) => setSession(sessionFromToken(token)),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      try {
        await request<{ ok: boolean }>("/auth/logout", { method: "POST" });
      } catch {
        // Auch wenn der Server nicht erreichbar ist, muss die Abmeldung örtlich
        // greifen — sonst bliebe auf einem verlorenen Gerät die Sitzung offen.
      }
    },
    onSettled: () => {
      setSession(null);
      queryClient.clear();
    },
  });
}

// ---------------------------------------------------------------------------
// Konto: zweiter Faktor, Passwort, Geräte
// ---------------------------------------------------------------------------
//
// Diese Endpunkte gab es seit dem ersten Tag, ohne dass die Oberfläche sie je
// aufrief. Die Anmeldung **prüft** den zweiten Faktor — einrichten konnte ihn
// niemand. Ebenso: kein Weg, das Passwort zu ändern, und keine Möglichkeit, ein
// verlorenes Gerät abzumelden.

export interface MfaSetupOut {
  /** Das Geheimnis im Klartext — für die Eingabe von Hand, wenn kein Scan geht. */
  secret: string;
  /** `otpauth://`-Adresse für den QR-Code. */
  provisioning_uri: string;
  /**
   * Ersatzcodes.
   *
   * Sie erscheinen **genau einmal**. Das ist keine technische Einschränkung,
   * sondern der Sinn der Sache: Wären sie später erneut abrufbar, wäre der
   * zweite Faktor nur so sicher wie der Zugang zum Konto.
   */
  recovery_codes: string[];
  message: string;
}

export interface SessionOut {
  id: string;
  device_label: string | null;
  ip_address: string | null;
  created_at: string;
  last_used_at: string | null;
  mfa_satisfied: boolean;
  is_current: boolean;
}

export function useMfaSetup(): UseMutationResult<MfaSetupOut, Error, void> {
  return useMutation({
    mutationFn: () => request<MfaSetupOut>("/auth/mfa/setup", { method: "POST" }),
  });
}

export function useMfaConfirm(): UseMutationResult<{ ok: boolean }, Error, { code: string }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => request<{ ok: boolean }>("/auth/mfa/confirm", { method: "POST", body }),
    // `mfa_enabled` steckt in der Kontoauskunft — ohne Erneuerung zeigt die
    // Ansicht nach dem Einrichten weiter „nicht eingerichtet".
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.me }),
  });
}

export function useChangePassword(): UseMutationResult<
  { ok: boolean },
  Error,
  { current_password: string; new_password: string }
> {
  return useMutation({
    mutationFn: (body) => request<{ ok: boolean }>("/auth/password", { method: "POST", body }),
  });
}

export function useSessions(): UseQueryResult<SessionOut[], Error> {
  return useQuery({
    queryKey: ["auth", "sessions"] as const,
    queryFn: () => request<SessionOut[]>("/auth/sessions"),
  });
}

export function useRevokeSession(): UseMutationResult<{ ok: boolean }, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id) => request<{ ok: boolean }>(`/auth/sessions/${id}`, { method: "DELETE" }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["auth", "sessions"] }),
  });
}
